
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ToneLevel, MovieContext, SUPPORTED_MODELS, ApiConnectionStatus } from '../types';

// --- Color Helpers ---
const hexToHsv = (hex: string) => {
    let r = 0, g = 0, b = 0;
    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
        r = parseInt(hex.substring(1, 3), 16);
        g = parseInt(hex.substring(3, 5), 16);
        b = parseInt(hex.substring(5, 7), 16);
    }
    r /= 255; g /= 255; b /= 255;
    
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s, v = max;
    const d = max - min;
    s = max === 0 ? 0 : d / max;

    if (max === min) {
        h = 0;
    } else {
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return { h: h * 360, s: s * 100, v: v * 100 };
};

const hsvToHex = (h: number, s: number, v: number) => {
    s /= 100; v /= 100;
    const c = v * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = v - c;
    let r = 0, g = 0, b = 0;
    
    if (0 <= h && h < 60) { r = c; g = x; b = 0; }
    else if (60 <= h && h < 120) { r = x; g = c; b = 0; }
    else if (120 <= h && h < 180) { r = 0; g = c; b = x; }
    else if (180 <= h && h < 240) { r = 0; g = x; b = c; }
    else if (240 <= h && h < 300) { r = x; g = 0; b = c; }
    else if (300 <= h && h < 360) { r = c; g = 0; b = x; }
    
    const toHex = (n: number) => {
        const hex = Math.round((n + m) * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

// --- Custom Color Picker Component ---
const CustomColorPicker: React.FC<{ color: string; onChange: (c: string) => void }> = ({ color, onChange }) => {
    const [hsv, setHsv] = useState(hexToHsv(color));
    const svRef = useRef<HTMLDivElement>(null);
    const hueRef = useRef<HTMLDivElement>(null);
    const [isDraggingSV, setIsDraggingSV] = useState(false);
    const [isDraggingHue, setIsDraggingHue] = useState(false);

    // Sync external color changes to internal HSV state (only if not dragging to avoid jitter)
    useEffect(() => {
        if (!isDraggingSV && !isDraggingHue) {
             setHsv(hexToHsv(color));
        }
    }, [color, isDraggingSV, isDraggingHue]);

    const handleSVUpdate = useCallback((e: MouseEvent | React.MouseEvent, isUp = false) => {
        if (!svRef.current) return;
        const rect = svRef.current.getBoundingClientRect();
        let x = e.clientX - rect.left;
        let y = e.clientY - rect.top;
        x = Math.max(0, Math.min(x, rect.width));
        y = Math.max(0, Math.min(y, rect.height));
        
        const s = (x / rect.width) * 100;
        const v = 100 - (y / rect.height) * 100;
        
        const newHsv = { ...hsv, s, v };
        setHsv(newHsv);
        onChange(hsvToHex(newHsv.h, newHsv.s, newHsv.v));
        if (isUp) setIsDraggingSV(false);
    }, [hsv, onChange]);

    const handleHueUpdate = useCallback((e: MouseEvent | React.MouseEvent, isUp = false) => {
        if (!hueRef.current) return;
        const rect = hueRef.current.getBoundingClientRect();
        let x = e.clientX - rect.left;
        x = Math.max(0, Math.min(x, rect.width));
        const h = (x / rect.width) * 360;
        
        const newHsv = { ...hsv, h };
        setHsv(newHsv);
        onChange(hsvToHex(newHsv.h, newHsv.s, newHsv.v));
         if (isUp) setIsDraggingHue(false);
    }, [hsv, onChange]);

    // Global Event Listeners for Dragging
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDraggingSV) handleSVUpdate(e);
            if (isDraggingHue) handleHueUpdate(e);
        };
        const handleMouseUp = (e: MouseEvent) => {
             if (isDraggingSV) handleSVUpdate(e, true);
             if (isDraggingHue) handleHueUpdate(e, true);
        };

        if (isDraggingSV || isDraggingHue) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingSV, isDraggingHue, handleSVUpdate, handleHueUpdate]);

    return (
        <div className="flex flex-col gap-3 w-full p-1 select-none">
            {/* Saturation/Value Area */}
            <div 
                ref={svRef}
                className="w-full h-32 relative rounded-lg cursor-crosshair overflow-hidden shadow-inner border border-gray-200 dark:border-gray-700"
                style={{ backgroundColor: `hsl(${hsv.h}, 100%, 50%)` }}
                onMouseDown={(e) => { setIsDraggingSV(true); handleSVUpdate(e); }}
            >
                <div className="absolute inset-0 bg-gradient-to-r from-white to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                <div 
                    className="absolute w-4 h-4 border-2 border-white rounded-full shadow-md transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                    style={{ left: `${hsv.s}%`, top: `${100 - hsv.v}%`, backgroundColor: color }}
                />
            </div>

            {/* Hue Slider */}
            <div 
                ref={hueRef}
                className="w-full h-4 relative rounded-full cursor-pointer shadow-inner border border-gray-200 dark:border-gray-700"
                style={{ background: 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)' }}
                onMouseDown={(e) => { setIsDraggingHue(true); handleHueUpdate(e); }}
            >
                <div 
                    className="absolute w-4 h-4 bg-white border border-gray-300 rounded-full shadow transform -translate-y-0 -translate-x-1/2 pointer-events-none"
                    style={{ left: `${(hsv.h / 360) * 100}%` }}
                />
            </div>

            {/* Inputs */}
            <div className="flex items-center gap-2">
                 <div className="w-8 h-8 rounded border border-gray-200 dark:border-gray-700 shadow-sm" style={{ backgroundColor: color }}></div>
                 <div className="flex-1 flex items-center border rounded-lg bg-white dark:bg-zinc-900 border-gray-200 dark:border-gray-700 overflow-hidden">
                     <span className="px-2 text-xs text-gray-400 font-mono">#</span>
                     <input 
                        type="text" 
                        value={color.replace('#', '')}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (/^[0-9A-Fa-f]{0,6}$/.test(val)) {
                                if (val.length === 6) onChange(`#${val}`);
                            }
                        }}
                        className="w-full py-1.5 text-xs font-mono uppercase bg-transparent outline-none text-gray-700 dark:text-gray-300"
                     />
                 </div>
            </div>
        </div>
    );
};


interface SettingsPanelProps {
  model: string;
  setModel: (m: string) => void;
  tone: ToneLevel;
  setTone: (t: ToneLevel) => void;
  context: MovieContext;
  setContext: (c: MovieContext) => void;
  isLocked: boolean;
  apiStatus: ApiConnectionStatus;
  onVerifyApi: () => void;
  apiKey: string;
  setApiKey: (k: string) => void;
  rangeStart: number;
  setRangeStart: (n: number) => void;
  rangeEnd: number;
  setRangeEnd: (n: number) => void;
  totalBlocks: number;
  // Batch Size Slider
  batchSize: number;
  setBatchSize: (n: number) => void;
  // Props V2
  charRelationships: string;
  setCharRelationships: (s: string) => void;
  isCensorshipEnabled: boolean;
  setIsCensorshipEnabled: (b: boolean) => void;
  isMetricConversionEnabled: boolean;
  setIsMetricConversionEnabled: (b: boolean) => void;
  // Clean Mode vs HI
  isCleanModeEnabled: boolean;
  setIsCleanModeEnabled: (b: boolean) => void;
  isHiTranslationEnabled: boolean;
  setIsHiTranslationEnabled: (b: boolean) => void;
  
  isLyricsTranslationEnabled: boolean;
  setIsLyricsTranslationEnabled: (b: boolean) => void;
  // Analysis
  onAutoAnalyze: () => void;
  isAnalyzing: boolean;
  // Filename Analysis
  filename: string;
  onFilenameAnalyze: () => void;
  isFilenameAnalyzing: boolean;
  // Relationship Analysis
  onRelationshipAnalyze: () => void;
  isRelationshipAnalyzing: boolean;
  // Theme & Range Actions
  isDarkMode: boolean;
  onSelectAllRange: () => void;
  // Bilingual
  isBilingual: boolean;
  setIsBilingual: (b: boolean) => void;
  // Subtitle Color
  subtitleColor: string;
  setSubtitleColor: (c: string) => void;
  isColorEnabled: boolean;
  setIsColorEnabled: (b: boolean) => void;
  // Editor Request
  onOpenRelEditor: () => void;
  // Dialect
  isSouthernStyle: boolean;
  setIsSouthernStyle: (b: boolean) => void;
  
  // Custom Style (New)
  customStyle: string;
  setCustomStyle: (s: string) => void;
  onNormalizeStyle: () => void;
  isNormalizingStyle: boolean;
  onOpenStyleEditor: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  model, setModel, tone, setTone, context, setContext, isLocked,
  apiStatus, onVerifyApi, apiKey, setApiKey, rangeStart, setRangeStart, rangeEnd, setRangeEnd, totalBlocks,
  batchSize, setBatchSize,
  charRelationships, setCharRelationships, isCensorshipEnabled, setIsCensorshipEnabled,
  isMetricConversionEnabled, setIsMetricConversionEnabled, 
  isCleanModeEnabled, setIsCleanModeEnabled,
  isHiTranslationEnabled, setIsHiTranslationEnabled,
  isLyricsTranslationEnabled, setIsLyricsTranslationEnabled,
  onAutoAnalyze, isAnalyzing, filename, onFilenameAnalyze, isFilenameAnalyzing,
  onRelationshipAnalyze, isRelationshipAnalyzing,
  isDarkMode, onSelectAllRange, isBilingual, setIsBilingual,
  subtitleColor, setSubtitleColor, isColorEnabled, setIsColorEnabled, onOpenRelEditor,
  isSouthernStyle, setIsSouthernStyle,
  customStyle, setCustomStyle, onNormalizeStyle, isNormalizingStyle, onOpenStyleEditor
}) => {
  
  const [showApiKey, setShowApiKey] = useState(false);

  // Derived warning for short context plot
  const isShortContext = context.plot.length > 0 && context.plot.length < 50;

  const handleToneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTone(parseInt(e.target.value) as ToneLevel);
  };

  const handleCleanModeChange = (checked: boolean) => {
    setIsCleanModeEnabled(checked);
    if (checked) {
        setIsHiTranslationEnabled(false);
    }
  };

  const handleHiTranslationChange = (checked: boolean) => {
      setIsHiTranslationEnabled(checked);
      if (checked) {
          setIsCleanModeEnabled(false);
      }
  };

  const getToneLabel = (t: ToneLevel) => {
    switch(t) {
      case 1: return "Lịch sự / PG-13";
      case 2: return "Tiêu chuẩn";
      case 3: return "Thân mật (Casual)";
      case 4: return "Bụi bặm";
      case 5: return "R-Rated (Tục)";
      default: return "";
    }
  };

  // Theme-based classes
  const cardClass = isDarkMode 
    ? "bg-[#18181b] border-white/5 hover:border-white/10" 
    : "bg-white border-gray-200 hover:border-gray-300 shadow-sm";
  const textLabelClass = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSubClass = isDarkMode ? "text-gray-400" : "text-gray-500";
  const inputClass = isDarkMode 
    ? "bg-zinc-950 border-white/10 text-white focus:ring-blue-500 placeholder-gray-600" 
    : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 placeholder-gray-400";
  const tooltipClass = isDarkMode ? "bg-black/50 border-white/5 text-gray-400" : "bg-gray-50 border-gray-200 text-gray-600";

  // Helper for Toggle Items
  const ToggleItem = ({ 
      label, 
      checked, 
      onChange, 
      iconPath, 
      colorClass = "indigo",
      children
  }: { 
      label: string, 
      checked: boolean, 
      onChange: (b: boolean) => void, 
      iconPath: string,
      colorClass?: string,
      children?: React.ReactNode
  }) => (
      <div className={`rounded-xl border transition-all ${checked 
          ? (isDarkMode ? `bg-${colorClass}-900/10 border-${colorClass}-500/30` : `bg-${colorClass}-50 border-${colorClass}-200`) 
          : (isDarkMode ? 'bg-zinc-900/40 border-white/5 hover:bg-white/5' : 'bg-gray-50 border-gray-200 hover:bg-gray-100')}
      `}>
          <div 
              className="flex items-center justify-between p-3 cursor-pointer"
              onClick={() => !isLocked && onChange(!checked)}
          >
              <div className="flex items-center gap-3">
                 <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors
                    ${checked 
                        ? (isDarkMode ? `bg-${colorClass}-500/20 text-${colorClass}-400` : `bg-${colorClass}-100 text-${colorClass}-600`) 
                        : (isDarkMode ? 'bg-white/5 text-gray-500' : 'bg-gray-200 text-gray-500')}
                 `}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={iconPath}></path>
                    </svg>
                 </div>
                 <span className={`text-xs font-medium transition-colors ${checked ? (isDarkMode ? 'text-white' : 'text-gray-900') : (isDarkMode ? 'text-gray-400' : 'text-gray-600')}`}>
                    {label}
                 </span>
              </div>
              
              {/* Toggle Switch */}
              <div className={`relative w-9 h-5 transition-colors rounded-full ${checked ? `bg-${colorClass}-600` : (isDarkMode ? 'bg-zinc-700' : 'bg-gray-300')}`}>
                  <div className={`absolute top-[2px] left-[2px] bg-white w-4 h-4 rounded-full transition-transform shadow-sm ${checked ? 'translate-x-4' : 'translate-x-0'}`}></div>
              </div>
          </div>
          {/* Extra Children (e.g. Color Picker) */}
          {checked && children && (
              <div className={`px-3 pb-3 pt-0 animate-fade-in-down`}>
                  {children}
              </div>
          )}
      </div>
  );

  return (
    <div className="space-y-6">
      
      {/* SECTION 1: KẾT NỐI API (Professional Redesign) */}
      <div className={`p-5 rounded-xl border transition-colors shadow-sm ${cardClass}`}>
        <div className="flex justify-between items-center mb-4">
            <h3 className={`text-sm font-bold uppercase tracking-wider flex items-center gap-2 ${textLabelClass}`}>
            <svg className={`w-4 h-4 ${textSubClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path></svg>
            API Connection
            </h3>
            <div className="flex items-center gap-2">
                 <span className={`text-[10px] px-2 py-0.5 rounded border ${apiStatus === 'valid' ? 'bg-green-500/10 text-green-500 border-green-500/20' : (isDarkMode ? 'bg-zinc-800 text-zinc-500 border-zinc-700' : 'bg-gray-100 text-gray-500 border-gray-200')}`}>
                    {apiStatus === 'valid' ? 'CONNECTED' : (apiStatus === 'invalid' ? 'ERROR' : 'NO TOKEN')}
                 </span>
            </div>
        </div>

        <div className="relative">
             <input 
                type={showApiKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={isLocked}
                placeholder="Nhập Google Gemini API Key..."
                className={`w-full border rounded-lg pl-10 pr-20 py-3 text-sm focus:outline-none focus:ring-1 transition-all ${inputClass}
                    ${apiStatus === 'valid' ? 'border-green-500/50 focus:border-green-500' : apiStatus === 'invalid' ? 'border-red-500/50 focus:border-red-500' : ''}
                `}
            />
            {/* Lock Icon */}
            <div className="absolute left-3 top-3.5 text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
            </div>
            
            {/* Actions Right */}
            <div className="absolute right-2 top-2 flex items-center gap-1">
                 <button 
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="p-1.5 rounded hover:bg-gray-200/20 text-gray-500 transition-colors"
                    title={showApiKey ? "Ẩn Key" : "Hiện Key"}
                 >
                     {showApiKey ? (
                         <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path></svg>
                     ) : (
                         <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                     )}
                 </button>
                 <button 
                    onClick={onVerifyApi}
                    disabled={apiStatus === 'verifying' || isLocked || !apiKey}
                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors 
                        ${apiStatus === 'valid' ? 'bg-green-600 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}
                    `}
                 >
                    {apiStatus === 'verifying' ? 'Checking...' : (apiStatus === 'valid' ? 'Saved' : 'Save')}
                 </button>
            </div>
        </div>

        <div className="flex justify-between items-center mt-3">
             <a 
                href="https://aistudio.google.com/api-keys" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[10px] text-blue-500 hover:text-blue-400 underline flex items-center gap-1"
            >
                Lấy Free API Key tại đây
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>
            </a>
            <span className={`text-[10px] italic ${textSubClass}`}>* Web không lưu token của bạn</span>
        </div>
      </div>

      {/* SECTION 2: CẤU HÌNH CỐT LÕI (MODEL & BATCH) */}
      <div className={`p-5 rounded-xl border transition-colors space-y-5 shadow-sm ${cardClass}`}>
        <h3 className={`text-sm font-bold uppercase tracking-wider mb-2 flex items-center gap-2 ${textLabelClass}`}>
           <svg className={`w-4 h-4 ${textSubClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
           Cấu hình Dịch
        </h3>
        
        {/* Model */}
        <div>
            <label className={`block text-xs font-medium mb-1.5 ${textSubClass}`}>Mô hình AI</label>
            <select 
            disabled={isLocked}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className={`w-full border rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none ${inputClass}`}
            >
            {SUPPORTED_MODELS.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
            ))}
            </select>
            <p className="text-xs text-amber-500/80 mt-2 leading-snug">
               * Ưu tiên sử dụng model Flash để tối ưu về quota, tốc độ và độ ổn định.
            </p>
        </div>

        {/* Batch Size Slider */}
        <div>
            <div className="flex justify-between items-end mb-1.5">
                <label className={`text-xs font-medium ${textSubClass}`}>Cụm dịch (Batch Size)</label>
                <span className="text-sm font-bold text-blue-500 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20">{batchSize} dòng</span>
            </div>
            <input 
              type="range" 
              min="10" 
              max="200" 
              step="10" 
              value={batchSize}
              disabled={isLocked}
              onChange={(e) => setBatchSize(parseInt(e.target.value))}
              className={`w-full h-2 rounded-lg appearance-none cursor-pointer accent-blue-500 ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-300'}`}
            />
            <p className={`text-xs mt-1.5 italic ${textSubClass}`}>
                * Mẹo: Cụm càng lớn Al hiểu ngữ cảnh cảng tốt, nhưng dễ bị lỗi Timeout hoặc Limit. Hãy giữ ở mức 10-50 để tiết kiệm request API/Token.
            </p>
        </div>

        {/* Tone Slider */}
        <div>
           <div className="flex justify-between items-end mb-1.5">
                <label className={`text-xs font-medium ${textSubClass}`}>Văn phong</label>
                <span className={`text-sm font-bold px-2 py-0.5 rounded border ${
                    tone >= 4 ? 'text-red-500 bg-red-500/10 border-red-500/20' : 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20'
                }`}>
                    {getToneLabel(tone)}
                </span>
            </div>
            <input 
            type="range" 
            min="1" 
            max="5" 
            step="1" 
            value={tone}
            disabled={isLocked}
            onChange={handleToneChange}
            className={`w-full h-2 rounded-lg appearance-none cursor-pointer accent-purple-500 ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-300'}`}
            />
             {/* Tone Description Tooltip */}
            <p className={`text-xs mt-2.5 p-3 rounded border leading-relaxed ${tooltipClass}`}>
                {tone === 1 && "Tích cực, lịch sự, thân thiện, vui tươi với trẻ em. Không xưng hô cực đoan hay thô thiển."}
                {tone === 2 && "Văn phong thân thiện với người xem dưới 13 tuổi. Sàng lọc và kiểm soát câu từ thô tục."}
                {tone === 3 && "Thân mật, bạn bè hoặc gia đình. Chỉ dùng 'Tao/Mày' nếu phân tích được quan hệ bạn bè cực thân hoặc ngữ cảnh văn phong thù địch. Nếu phim bậy/bựa thì có thể sẽ không tiết giảm tình huống để đảm bảo nguyên tác."}
                {tone === 4 && "Bụi bặm, đường phố, giang hồ. Dùng 'Tao/Mày' nếu bối cảnh thù địch, chửi thề nếu ngữ cảnh phù hợp với nguyên tác."}
                {tone === 5 && "Tự do văng tục,chửi thề sang tiếng lóng Việt Nam một cách tự nhiên. Có thể kịch tính và cực đoan hoá một số câu cảm thán."}
            </p>
        </div>
      </div>

      {/* SECTION 3: PHẠM VI DỊCH */}
      <div className={`p-5 rounded-xl border transition-colors shadow-sm ${cardClass}`}>
        <div className="flex justify-between items-center mb-3">
            <h3 className={`text-sm font-bold uppercase tracking-wider flex items-center gap-2 ${textLabelClass}`}>
                <svg className={`w-4 h-4 ${textSubClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path></svg>
                Phạm vi (Range)
            </h3>
            {totalBlocks > 0 && (
                <button
                    onClick={onSelectAllRange}
                    className="text-xs text-blue-500 hover:text-blue-400 font-medium underline"
                >
                    Chọn tất cả
                </button>
            )}
        </div>
        {totalBlocks > 0 ? (
          <div className="flex items-center gap-3">
            <input 
              type="number" 
              min="1" 
              max={totalBlocks}
              value={rangeStart}
              onChange={(e) => setRangeStart(Math.max(1, parseInt(e.target.value)))}
              disabled={isLocked}
              className={`w-full border rounded p-2 text-center text-sm font-mono focus:ring-1 focus:ring-blue-500 ${inputClass}`}
            />
            <span className={`${textSubClass} font-bold text-lg`}>→</span>
            <input 
              type="number" 
              min={rangeStart} 
              max={totalBlocks}
              value={rangeEnd}
              onChange={(e) => setRangeEnd(Math.min(totalBlocks, parseInt(e.target.value)))}
              disabled={isLocked}
              className={`w-full border rounded p-2 text-center text-sm font-mono focus:ring-1 focus:ring-blue-500 ${inputClass}`}
            />
          </div>
        ) : (
          <div className={`text-xs italic text-center py-2 ${textSubClass}`}>Upload file để chọn dòng</div>
        )}
        <p className={`text-[10px] mt-2 italic ${textSubClass}`}>
            * Khuyến nghị: Với tài khoản Free, nên dịch từng đoạn nhỏ (dưới 400 dòng) để tránh lỗi Limit. Có thể đổi nhiều API Key để dịch liên tục.
        </p>
      </div>

      {/* SECTION 4: NGỮ CẢNH & PHÂN TÍCH */}
      <div className={`p-5 rounded-xl border transition-colors space-y-4 shadow-sm ${cardClass}`}>
        <h3 className={`text-sm font-bold uppercase tracking-wider mb-1 flex items-center gap-2 ${textLabelClass}`}>
           <svg className={`w-4 h-4 ${textSubClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path></svg>
           Bối cảnh (Context)
        </h3>
       
        {/* Title Input */}
        <div className="flex gap-2">
            <input 
                type="text"
                placeholder="Tên phim (Hoặc nhập tay)"
                value={context.title}
                onChange={(e) => setContext({...context, title: e.target.value})}
                disabled={isLocked}
                className={`flex-1 border rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-indigo-500 ${inputClass}`}
            />
            <button 
                onClick={onFilenameAnalyze}
                disabled={isLocked || isFilenameAnalyzing || !apiKey || !filename || filename === "subtitle.srt"}
                className="px-3 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 text-indigo-500 rounded-lg transition-colors"
                title="AI đọc tên file"
            >
                {isFilenameAnalyzing ? (
                     <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                ) : (
                     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                )}
            </button>
        </div>

        {/* Plot */}
        <textarea 
          placeholder="Tóm tắt nội dung để AI dịch đúng vai vế..."
          value={context.plot}
          onChange={(e) => setContext({...context, plot: e.target.value})}
          disabled={isLocked}
          className={`w-full border rounded-lg p-2.5 text-sm h-20 resize-none focus:ring-1 focus:ring-indigo-500 custom-scrollbar leading-relaxed ${inputClass}`}
        />

        {/* AI Deep Analyze */}
        {totalBlocks > 0 && (
            <button 
                onClick={onAutoAnalyze}
                disabled={isLocked || isAnalyzing || !apiKey}
                className={`w-full py-2 rounded-lg text-xs font-bold uppercase tracking-wide border transition-all flex justify-center items-center gap-2
                    ${isDarkMode 
                        ? 'bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-900 disabled:text-gray-600 text-gray-300 border-white/5' 
                        : 'bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 border-gray-300'}
                `}
            >
               {isAnalyzing ? "Đang đọc nội dung..." : "✨ AI: Phân tích nội dung Sub"}
            </button>
        )}
        
        {/* Relationships */}
        <div className={`pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center mb-1.5">
                <label className={`text-xs font-medium ${textSubClass}`}>Quan hệ nhân vật (Tùy chọn)</label>
                <div className="flex gap-2">
                    <button
                        onClick={onRelationshipAnalyze}
                        disabled={isLocked || isRelationshipAnalyzing || !apiKey || !context.title}
                        className="text-[10px] bg-purple-500/10 hover:bg-purple-500/20 text-purple-500 px-2 py-0.5 rounded border border-purple-500/30 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Tự động phân tích quan hệ nhân vật dựa trên tên phim"
                    >
                        {isRelationshipAnalyzing ? (
                            <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                        ) : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                        )}
                        AI Gợi ý
                    </button>
                    {charRelationships && (
                        <button
                            onClick={onOpenRelEditor}
                            className="text-[10px] bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 px-2 py-0.5 rounded border border-blue-500/30 transition-colors flex items-center gap-1"
                            title="Mở rộng để chỉnh sửa"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path></svg>
                            Mở rộng
                        </button>
                    )}
                </div>
            </div>
            
            {isShortContext && (
                <div className="mb-2 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-600 flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                    <span>Đoạn ngắn: Hãy nhập thêm ngữ cảnh.</span>
                </div>
            )}

            <textarea 
            placeholder="John (sếp) - Mary (lính). Tom - Jerry (kẻ thù)..."
            value={charRelationships}
            disabled={isLocked}
            onChange={(e) => setCharRelationships(e.target.value)}
            className={`w-full border rounded-lg p-2.5 text-sm h-24 resize-none leading-relaxed custom-scrollbar ${inputClass} ${isDarkMode ? 'text-amber-100/90 focus:ring-amber-500/50' : 'text-amber-900 focus:ring-amber-500/50'}`}
            />
        </div>
      </div>

      {/* SECTION 5: CÁC TÙY CHỌN KHÁC - REDESIGNED */}
      <div className="space-y-2 pt-1">
         <h3 className={`text-sm font-bold uppercase tracking-wider mb-2 flex items-center gap-2 ${textLabelClass}`}>
            <svg className={`w-4 h-4 ${textSubClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path></svg>
            Tùy chọn khác
         </h3>
         
         {/* Bilingual (Moved here & Added Color Picker) */}
         <ToggleItem 
            label="Tạo phụ đề Song ngữ (Anh-Việt)" 
            checked={isBilingual} 
            onChange={setIsBilingual} 
            iconPath="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" 
            colorClass="indigo"
         >
            {/* Color Picker for Bilingual */}
             <div className="mt-2 flex items-start justify-between p-2 rounded-lg bg-black/10 border border-black/5">
                <span className={`text-[10px] font-medium mt-1 ${textSubClass}`}>Đổi màu phần dịch (Tiếng Việt)</span>
                <div className="flex flex-col items-end gap-2 w-full max-w-[220px]">
                    <div className="flex items-center gap-2 self-end mb-1">
                        <span className={`text-[10px] ${textSubClass}`}>Bật màu</span>
                         <input 
                            type="checkbox" 
                            checked={isColorEnabled}
                            onChange={(e) => setIsColorEnabled(e.target.checked)}
                            className={`w-3 h-3 rounded ${isDarkMode ? 'bg-zinc-800' : 'bg-white'}`}
                        />
                    </div>
                    {isColorEnabled && (
                        <CustomColorPicker color={subtitleColor} onChange={setSubtitleColor} />
                    )}
                </div>
             </div>
         </ToggleItem>
         
         {/* Color Picker for Standard Mode (If Not Bilingual) */}
         {!isBilingual && (
             <ToggleItem 
                label="Đổi màu phụ đề (Mã HEX)" 
                checked={isColorEnabled} 
                onChange={setIsColorEnabled} 
                iconPath="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                colorClass="yellow"
            >
                <div className="mt-2 flex items-start justify-between p-2 rounded-lg bg-black/10 border border-black/5">
                    <span className={`text-[10px] font-medium mt-1 ${textSubClass}`}>Chọn màu hiển thị</span>
                    <div className="w-full max-w-[220px]">
                        <CustomColorPicker color={subtitleColor} onChange={setSubtitleColor} />
                    </div>
                 </div>
            </ToggleItem>
         )}

         {/* Southern Style Dialect */}
         <ToggleItem 
            label="Văn phong miền Nam (Ba, muỗng, xài...)" 
            checked={isSouthernStyle} 
            onChange={setIsSouthernStyle} 
            iconPath="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z" 
            colorClass="orange"
         />

         {/* Censorship */}
         <ToggleItem 
            label="Che nguyên âm từ tục tĩu bằng dấu '*'" 
            checked={isCensorshipEnabled} 
            onChange={setIsCensorshipEnabled} 
            iconPath="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" 
            colorClass="red"
         />

         {/* Metric */}
         <ToggleItem 
            label="Tự động đổi đơn vị đo (km, kg, °C...)" 
            checked={isMetricConversionEnabled} 
            onChange={setIsMetricConversionEnabled} 
            iconPath="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" 
            colorClass="blue"
         />

        {/* Clean Mode */}
        <ToggleItem 
            label="Dọn dẹp (chú thích) [mô tả] âm thanh" 
            checked={isCleanModeEnabled} 
            onChange={handleCleanModeChange} 
            iconPath="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
            colorClass="orange"
         />

         {/* HI Translation */}
         <ToggleItem 
            label="Dịch chú thích Hearing Impaired [âm thanh]" 
            checked={isHiTranslationEnabled} 
            onChange={handleHiTranslationChange} 
            iconPath="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" 
            colorClass="teal"
         />

         {/* Lyrics Translation */}
         <ToggleItem 
            label="Phát hiện và dịch lời bài hát (♪, ♫)" 
            checked={isLyricsTranslationEnabled} 
            onChange={setIsLyricsTranslationEnabled} 
            iconPath="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" 
            colorClass="pink"
         />
      </div>

      {/* SECTION 6: CÁ NHÂN HOÁ VĂN PHONG (New) */}
      <div className={`p-5 rounded-xl border transition-colors space-y-3 shadow-sm ${cardClass}`}>
        <div className="flex justify-between items-center mb-1">
            <h3 className={`text-sm font-bold uppercase tracking-wider flex items-center gap-2 ${textLabelClass}`}>
                <svg className={`w-4 h-4 ${textSubClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                Cá nhân hoá văn phong
            </h3>
            <div className="flex gap-2">
                <button
                    onClick={onNormalizeStyle}
                    disabled={isLocked || isNormalizingStyle || !apiKey || !customStyle}
                    className="text-[10px] bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 px-2 py-0.5 rounded border border-blue-500/30 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="AI tự động sắp xếp lại các quy tắc văn phong"
                >
                    {isNormalizingStyle ? (
                        <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                    ) : (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                    )}
                    AI chuẩn hoá prompt
                </button>
                {customStyle && (
                    <button
                        onClick={onOpenStyleEditor}
                        className="text-[10px] bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 px-2 py-0.5 rounded border border-emerald-500/30 transition-colors flex items-center gap-1"
                        title="Mở rộng để chỉnh sửa"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path></svg>
                        Mở rộng
                    </button>
                )}
            </div>
        </div>
        <p className={`text-[10px] italic ${textSubClass} leading-tight mb-2`}>
            Nhập các yêu cầu riêng (VD: Dịch từ "A" phải là "B", văn phong giống kênh "XYZ"). Quy tắc này sẽ được model ưu tiên cao nhất.
        </p>
        <textarea 
            placeholder="Dịch 'Hello' thành 'Xin chào ngài'. Dùng từ ngữ hài hước kiểu phim Châu Tinh Trì..."
            value={customStyle}
            disabled={isLocked}
            onChange={(e) => setCustomStyle(e.target.value)}
            className={`w-full border rounded-lg p-2.5 text-sm h-28 resize-none leading-relaxed custom-scrollbar ${inputClass} ${isDarkMode ? 'text-blue-100/90 focus:ring-blue-500/50' : 'text-blue-900 focus:ring-blue-500/50'}`}
        />
      </div>
    </div>
  );
};

export default SettingsPanel;
